import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useSelector } from 'react-redux';
import { selectors } from '@altermaind/core/i18n';
import { DEFAULT_LANGUAGE } from '../constants';
import { getLocaleAndFormatDateFromTimeZone } from '../helpers/getLocaleAndFormatDateFromTimeZone';

dayjs.extend(customParseFormat);

type UseLocalizedFullDate = {
  stringToLocalizedFullDate: (date?: string) => string | undefined;
  dateToLocalizedFullDate: (date?: Date) => string | undefined;
};

const capitalizeFirstLetter = (value: string): string => {
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * Provides utility functions to format dates using the current application language.
 *
 * String dates are expected to be already localized using the format returned by
 * `getLocaleAndFormatDateFromTimeZone().dateFormat`, for example `DD/MM/YYYY`
 * for `Europe/Rome`.
 *
 * The output follows the date order and month naming rules of the current language.
 * For example:
 * - Italian: `22 maggio 2026`
 * - English: `May 22, 2026`
 *
 * @returns {UseLocalizedFullDate} An object containing date formatting utilities.
 *
 * @example
 * const { stringToLocalizedFullDate } = useLocalizedFullDate();
 *
 * stringToLocalizedFullDate('22/05/2026');
 * // it: "22 Maggio 2026"
 * // en: "May 22, 2026"
 */
export const useLocalizedFullDate = (): UseLocalizedFullDate => {
  const language = useSelector(selectors.selectCurrentLanguage) || DEFAULT_LANGUAGE;
  const { dateFormat } = getLocaleAndFormatDateFromTimeZone();

  const formatter = useMemo(
    () =>
      new Intl.DateTimeFormat(language, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    [language],
  );

  const formatDate = useCallback(
    (date?: Date): string | undefined => {
      if (!date || Number.isNaN(date.getTime())) {
        return undefined;
      }

      return capitalizeFirstLetter(formatter.format(date));
    },
    [formatter],
  );

  const stringToLocalizedFullDate = useCallback(
    (date?: string): string | undefined => {
      if (!date) {
        return undefined;
      }

      const parsedDate = dayjs(date, dateFormat, true);

      if (!parsedDate.isValid()) {
        return undefined;
      }

      return formatDate(parsedDate.toDate());
    },
    [dateFormat, formatDate],
  );

  const dateToLocalizedFullDate = useCallback(
    (date?: Date): string | undefined => {
      return formatDate(date);
    },
    [formatDate],
  );

  return {
    stringToLocalizedFullDate,
    dateToLocalizedFullDate,
  };
};
